import React, { useState } from 'react';
import { Button, Space, Upload, Modal, Table, Typography, Alert, message } from 'antd';
import { DownloadOutlined, UploadOutlined, FileExcelOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dataService from '../services/dataService';

const { Text, Paragraph } = Typography;

// Export / template / import for one section, as a row of buttons a page can drop
// in beside its own heading.
//
// The import result is shown rather than reduced to "done": a doctor importing
// years of old records needs to see which rows the file could not use, and which
// line of their spreadsheet each one is on.
const DataTransfer = ({ section, onImported, size = 'middle' }) => {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(null);
    const [result, setResult] = useState(null);

    const run = async (job, work) => {
        setBusy(job);
        try {
            return await work();
        } catch (error) {
            message.error(error.response?.data?.message || error.message || t('error'));
            return null;
        } finally {
            setBusy(null);
        }
    };

    const handleExport = () => run('export', () => dataService.exportSection(section));
    const handleTemplate = () => run('template', () => dataService.downloadTemplate(section));

    const handleImport = async (file) => {
        const response = await run('import', () => dataService.importSection(section, file));

        if (response) {
            setResult(response.data);
            // The page behind refreshes even on a partial import - the rows that did
            // land are real, and leaving a stale table would hide them.
            if (onImported) onImported();
        }

        // Stops antd uploading the file itself; dataService has already sent it.
        return false;
    };

    return (
        <>
            <Space wrap>
                <Button
                    icon={<DownloadOutlined />}
                    size={size}
                    loading={busy === 'export'}
                    onClick={handleExport}
                >
                    {t('exportExcel')}
                </Button>

                <Upload
                    accept=".xlsx"
                    showUploadList={false}
                    beforeUpload={handleImport}
                >
                    <Button icon={<UploadOutlined />} size={size} loading={busy === 'import'}>
                        {t('importExcel')}
                    </Button>
                </Upload>

                <Button
                    type="link"
                    icon={<FileExcelOutlined />}
                    size={size}
                    loading={busy === 'template'}
                    onClick={handleTemplate}
                >
                    {t('downloadTemplate')}
                </Button>
            </Space>

            <Modal
                open={Boolean(result)}
                title={t('importResult')}
                onCancel={() => setResult(null)}
                onOk={() => setResult(null)}
                cancelButtonProps={{ style: { display: 'none' } }}
                width={720}
            >
                {result && (
                    <>
                        <Alert
                            type={result.failed > 0 ? 'warning' : 'success'}
                            showIcon
                            message={
                                <Space wrap>
                                    <Text>{t('rowsRead')}: <Text strong>{result.read}</Text></Text>
                                    <Text>{t('created')}: <Text strong>{result.created}</Text></Text>
                                    <Text>{t('updated')}: <Text strong>{result.updated}</Text></Text>
                                    <Text>{t('failed')}: <Text strong>{result.failed}</Text></Text>
                                </Space>
                            }
                            description={result.failed > 0 ? t('importPartialHint') : undefined}
                        />

                        {result.errors?.length > 0 && (
                            <>
                                <Paragraph style={{ marginTop: 16, marginBottom: 8 }}>
                                    <Text type="secondary">{t('rowsNotImported')}</Text>
                                </Paragraph>
                                <Table
                                    size="small"
                                    rowKey={(row) => `${row.row}-${row.message}`}
                                    dataSource={result.errors}
                                    pagination={result.errors.length > 8 ? { pageSize: 8 } : false}
                                    columns={[
                                        { title: t('rowNumber'), dataIndex: 'row', key: 'row', width: 90 },
                                        { title: t('problem'), dataIndex: 'message', key: 'message' }
                                    ]}
                                />
                            </>
                        )}
                    </>
                )}
            </Modal>
        </>
    );
};

export default DataTransfer;
